<template>
	<component :is="as" v-bind="$props">
		<slot />
	</component>
</template>

<script>
import { resolveComponent } from 'vue'
import { useRouter } from '#app'

import { pathResolver } from './helpers'

function isExternalHref(href) {
	return /^https?:\/\//.test(href) || href.startsWith('mailto:') || href.startsWith('tel:')
}

function createAnchorProps(href) {
	const normalizedHref = href || '#'

	return {
		href: normalizedHref,
		...(normalizedHref.startsWith('http') ? { target: '_blank' } : {}),
	}
}

function useHyperlink(to) {
	const { currentRoute } = useRouter()

	try {
		if (typeof to === 'string') {
			if (isExternalHref(to)) {
				return {
					as: 'a',
					$props: createAnchorProps(to),
				}
			}

			return {
				as: resolveComponent('NuxtLink'),
				$props: {
					to,
					activeClass: '-active',
					exactActiveClass: '-exact',
				},
			}
		}

		const { type } = to

		if (type === 'external') {
			const href = to.url ?? to.href

			return {
				as: 'a',
				$props: createAnchorProps(href),
			}
		}

		if (type === 'internal') {
			const resolvedPath = to.path ?? to.url ?? pathResolver(to.document, to.slug)

			return {
				as: resolveComponent('NuxtLink'),
				$props: {
					to: resolvedPath,
					activeClass: '-active',
					exactActiveClass: '-exact',
				},
			}
		}

		if (type === 'form') {
			return {
				as: resolveComponent('NuxtLink'),
				$props: {
					to: `${currentRoute.value.path}?drawer=${to.id}`,
				},
			}
		}

		if (type === 'pdf') {
			return {
				as: 'a',
				$props: {
					href: to.url,
					target: '_blank',
				},
			}
		}

		throw new Error('Hyperlink: Unable to render link type.')
	} catch (e) {
		console.error(e)

		return { as: 'span', $props: { 'data-unknown-link': '' } }
	}
}
</script>
<script setup>
const props = defineProps({
	to: { type: [String, Object], required: true },
})

const { as, $props } = useHyperlink(props.to)
</script>
